import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/components/ui';

/**
 * The last line between a bad response and a blank screen.
 *
 * The client validates the routes it can, but validation is per-route and
 * shallow in places: a nested field that drifts, a required array that arrives
 * absent, and the page throws mid-render. React unmounts the whole tree on an
 * uncaught render error, so the result was the entire application replaced by
 * nothing at all, with the operator given no statement of what happened and no
 * way out but the browser's own reload.
 *
 * This was not hypothetical. Serving the bucket page a toolset whose stray
 * entry lacked its required `reasons` array reproduced it exactly: a white
 * screen, and in the console a TypeError reading 'map' of undefined.
 *
 * It says what is known and what is not. It does not claim the backend is
 * broken, because the fault is as likely to be this build reading a shape it
 * does not understand, and it does not offer a retry that cannot work: the
 * same render with the same data fails the same way, so the honest moves are
 * to reload or to leave.
 */
interface Props {
  children: ReactNode;
  /** What the operator was looking at, named in their words. */
  surface: string;
  /**
   * Changes when the route does. A boundary that latches forever turns one
   * bad page into a dead application, so navigating away clears it.
   */
  resetKey?: string;
}

interface State {
  error: Error | null;
  /** The resetKey the current error belongs to. */
  at: string | undefined;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, at: undefined };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (state.error && state.at === undefined) return { at: props.resetKey };
    // Navigated somewhere else: this page never failed, so stop showing that it did.
    if (state.error && state.at !== props.resetKey) return { error: null, at: undefined };
    return null;
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Never swallowed. Whoever is debugging needs the component stack, and a
    // boundary that hides the error is worse than the blank screen it replaced.
    console.error(`Render failed on ${this.props.surface}:`, error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="grid min-h-0 flex-1 place-items-center p-8">
        <div className="max-w-lg text-center">
          <h2 className="text-h2 text-ink">{this.props.surface} could not be drawn</h2>
          <p className="mt-2 text-small text-ink-2">
            Something in this build failed while rendering what the backend returned. Nothing was changed by it, and the
            rest of the app still works. This is as likely to be a shape this build does not understand as a fault in
            the data.
          </p>
          <p className="mono mt-3 break-words rounded border border-line bg-panel-2 px-3 py-2 text-caption text-ink-3">
            {this.state.error.message}
          </p>
          <div className="mt-4 flex justify-center gap-2">
            {/* No retry: the same render over the same data fails the same
                way, and a button that cannot work is worse than none. */}
            <Button onClick={() => window.location.reload()}>Reload this page</Button>
            <Button variant="ghost" onClick={() => window.history.back()}>
              Go back
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
